type DroppedFileSystemEntry = {
  isDirectory: boolean;
  isFile: boolean;
  name: string;
  createReader?: () => DroppedDirectoryReader;
  file?: (success: (file: File) => void, error: (error: DOMException) => void) => void;
};

type DroppedDirectoryReader = {
  readEntries: (
    success: (entries: DroppedFileSystemEntry[]) => void,
    error: (error: DOMException) => void
  ) => void;
};

type EntryDataTransferItem = {
  webkitGetAsEntry?: () => DroppedFileSystemEntry | null;
};

const readEntriesBatch = (reader: DroppedDirectoryReader) =>
  new Promise<DroppedFileSystemEntry[]>((resolve, reject) => reader.readEntries(resolve, reject));

const readAllDirectoryEntries = async (reader: DroppedDirectoryReader) => {
  const entries: DroppedFileSystemEntry[] = [];
  let batch = await readEntriesBatch(reader);
  while (batch.length) {
    entries.push(...batch);
    batch = await readEntriesBatch(reader);
  }
  return entries;
};

const fileFromEntry = (entry: DroppedFileSystemEntry, relativePath: string) =>
  new Promise<File>((resolve, reject) => {
    if (!entry.file) {
      reject(new Error('Dropped file entry could not be read'));
      return;
    }

    entry.file((file) => {
      Object.defineProperty(file, 'webkitRelativePath', {
        configurable: true,
        value: `${relativePath}${entry.name}`,
      });
      resolve(file);
    }, reject);
  });

const filesFromEntry = async (
  entry: DroppedFileSystemEntry,
  relativePath = ''
): Promise<File[]> => {
  if (entry.isFile) {
    return [await fileFromEntry(entry, relativePath)];
  }

  if (!entry.isDirectory || !entry.createReader) {
    return [];
  }

  const children = await readAllDirectoryEntries(entry.createReader());
  const childFiles = await Promise.all(
    children.map((child) => filesFromEntry(child, `${relativePath}${entry.name}/`))
  );
  return childFiles.flat();
};

export const filesFromDataTransfer = async (dataTransfer: DataTransfer): Promise<File[]> => {
  const entries = Array.from(dataTransfer.items)
    .map((item) => (item as unknown as EntryDataTransferItem).webkitGetAsEntry?.() ?? null)
    .filter((entry): entry is DroppedFileSystemEntry => entry !== null);

  if (!entries.length) {
    return Array.from(dataTransfer.files);
  }

  const nestedFiles = await Promise.all(entries.map((entry) => filesFromEntry(entry)));
  return nestedFiles.flat();
};
